import styled from 'styled-components';
import FilterIcon from '@/assets/mo/Filter.svg?react';
import ReviewTag from '@/components/mo/Review/ReviewTag';
import ReviewMetaTag from '@/components/mo/Review/ReviewMetaTag';
import { useQuery } from '@tanstack/react-query';
import { fetchReviewList } from '@/api/review';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Spin } from 'antd';
import EyesIcon from '@/assets/mo/eyes.svg?react';
import { useState } from 'react';

interface FilterBarProps {
  onOpenModal: () => void;
}

const FilterBar = ({ onOpenModal }: FilterBarProps) => {
  const [sort, setSort] = useState<'recent' | 'view'>('recent');
  const [visibleCount, setVisibleCount] = useState(5);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const type = searchParams.get('type') ?? '전체';
  const issue = searchParams.get('issue') ?? '전체';

  const getFilteredTags = () => {
    const insuranceTags = type !== '전체' && type !== '기타' ? [type] : [];
    const accidentTags = issue !== '전체' && issue !== '기타' ? [issue] : [];
    const allTags = [...insuranceTags, ...accidentTags];
    return allTags.length > 0 ? allTags : undefined;
  };

  const { data, isLoading, isError } = useQuery({
    queryKey: ['reviewList', sort, type, issue],
    queryFn: () =>
      fetchReviewList({
        page: 0,
        size: 200,
        sort: [sort === 'recent' ? 'incidentDate,desc' : 'viewCount,desc'],
        tags: getFilteredTags(),
        keyword: '',
      }),
  });

  const reviewList = data?.content ?? [];

  const handleMore = () => setVisibleCount((prev) => prev + 5);

  if (isLoading)
    return (
      <SpinWrapper>
        <Spin size="large" />
      </SpinWrapper>
    );

  if (isError) return <div>에러 발생</div>;

  return (
    <Wrapper>
      <InnerColumn>
        <HeaderRow>
          <FilterBadge onClick={onOpenModal}>
            <FilterIcon style={{ width: '16px', height: '16px' }} />
            <FilterText>필터</FilterText>
          </FilterBadge>

          <SelectedFilterRow>
            {[type, issue]
              .filter((value) => value && value !== '전체')
              .map((value) => (
                <FilterTag key={value}>{value}</FilterTag>
              ))}
          </SelectedFilterRow>
        </HeaderRow>

        <SortRow>
          <SortButton selected={sort === 'recent'} onClick={() => setSort('recent')}>
            최신순
          </SortButton>
          <SortButton selected={sort === 'view'} onClick={() => setSort('view')}>
            조회순
          </SortButton>
        </SortRow>

        {reviewList.length === 0 ? (
          <NoDataMessage>데이터가 없습니다.</NoDataMessage>
        ) : (
          reviewList.slice(0, visibleCount).map((review) => (
            <CardWrapper key={review.id} onClick={() => navigate(`/review/${review.id}`)}>
              <CardTopRow>
                <CardTopTags>
                  {review.tags.map((tag) => (
                    <ReviewTag key={tag.id} label={`#${tag.tag}`} />
                  ))}
                </CardTopTags>
                <ViewCountWrapper>
                  <EyesIcon />
                  <ViewCountText>{review.viewCount.toLocaleString()}</ViewCountText>
                </ViewCountWrapper>
              </CardTopRow>
              <CardContents>
                <CardTitle>{review.title}</CardTitle>
                <CardDesc>{review.content}</CardDesc>
              </CardContents>
              <MetaRow>
                <ReviewMetaTag
                  label={
                    review.issues[0]?.isPayoutApproved
                      ? review.issues[0]?.totalFee
                        ? `${Number(review.issues[0].totalFee).toLocaleString()}만원 지급`
                        : '지급'
                      : '부지급'
                  }
                />
                <ReviewDate>
                  {review.incidentDate
                    ? new Date(review.incidentDate).toLocaleDateString('ko-KR')
                    : ''}
                </ReviewDate>
              </MetaRow>
            </CardWrapper>
          ))
        )}
      </InnerColumn>

      {reviewList.length > visibleCount && (
        <LoadMoreButton onClick={handleMore}>
          <LoadMoreText>더보기</LoadMoreText>
        </LoadMoreButton>
      )}
    </Wrapper>
  );
};

export default FilterBar;

// ⭐️ 스타일 생략 없이 유지 (기존 코드 그대로 사용)

// ----------------- 스타일 -----------------
const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  align-self: stretch;
  padding: 16px 0;
`;

const InnerColumn = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  align-self: stretch;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  align-self: stretch;
`;

const FilterBadge = styled.div`
  display: flex;
  padding: 4px 10px;
  justify-content: flex-end;
  align-items: center;
  gap: 4px;
  border-radius: 99px;
  background: var(--gray-ui_gray01, #191919);
`;

const FilterText = styled.span`
  color: var(--Primary-white, #fff);
  font-size: 14px;
  font-weight: 500;
  line-height: 120%;
  letter-spacing: -0.28px;
`;

const SelectedFilterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  padding: 12px 0;
  height: auto;
`;

const FilterTag = styled.div`
  padding: 4px 8px;
  border-radius: 99px;
  border: 1px solid var(--table-line-table_line03, #d1d1d1);
  background: #fff;
  color: #6f6f6f;
  font-size: 12px;
  font-weight: 500;
`;

const SortRow = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`;

const SortButton = styled.div<{ selected: boolean }>`
  display: flex;
  height: 48px;
  padding: 10px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  user-select: none;
  color: ${({ selected }) => (selected ? '#000C24' : '#6F6F6F')};
  font-size: 12px;
  font-weight: 700;
  line-height: 120%;
`;

const CardWrapper = styled.div`
  display: flex;
  padding: 20px 8px;
  flex-direction: column;
  gap: 8px;
  border-bottom: 1px solid #d1d1d1;
  cursor: pointer;
`;

const CardTopTags = styled.div`
  display: flex;
  gap: 8px;
  align-self: stretch;
`;

const CardContents = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-self: stretch;
`;

const CardTitle = styled.div`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  overflow: hidden;
  color: #191919;
  font-size: 20px;
  font-weight: 700;
  text-overflow: ellipsis;
  line-height: 120%;
  text-align: start;
`;

const CardDesc = styled.div`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  color: #3a3a3a;
  font-size: 16px;
  font-weight: 400;
  text-overflow: ellipsis;
  line-height: 140%;
  text-align: start;
`;

const NoDataMessage = styled.div`
  width: 100%;
  padding: 40px 0;
  text-align: center;
  color: #999;
  font-size: 14px;
`;

const LoadMoreButton = styled.div`
  display: flex;
  padding: 14px 24px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  background: #f3f3f3;
  border-radius: 999px;
  margin: 24px auto 0;
  cursor: pointer;
`;

const LoadMoreText = styled.div`
  color: #999;
  font-size: 14px;
  font-weight: 600;
  line-height: 120%;
  letter-spacing: -0.2px;
`;

const SpinWrapper = styled.div`
  width: 100%;
  padding: 80px 0;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const CardTopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  align-self: stretch;
  width: 100%;
`;

const ViewCountWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ViewCountText = styled.span`
  color: var(--gray-ui_gray05, #999);
  text-align: center;
  font-family: 'Spoqa Han Sans Neo';
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 120%; /* 16.8px */
  letter-spacing: -0.28px;
`;

const MetaRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
`;

const ReviewDate = styled.div`
  color: var(--gray-ui_gray05, #999);
  text-align: center;
  font-family: 'Spoqa Han Sans Neo';
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 120%; /* 16.8px */
  letter-spacing: -0.28px;
`;
